/**
 * 파일명 : com0110m.tsx
 * 설명 : 결재문서조회 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2020-08-10   윤성수     최초작성
 *     
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Com0110mAction } from '../../../redux/slice/com/com0110m_slice';
import { IApprDocItemState } from '../../../redux/models/com/com0110m_model';

/*  AntD  */
import { Button, Space, Row, Col, Layout, Typography, Table } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

/*  업무  */
import * as common from 'libs/common';


const Com0108m = () => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch = useAppDispatch();
    const apprDocList = useAppSelector((state) => state.Com0110m.apprDocList);
    const current     = useAppSelector((state) => state.Com0110m.current);
    const pageSize    = useAppSelector((state) => state.Com0110m.pageSize);


    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        fn_Search();
    }, []);


    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text }    = Typography;

    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: "center" }}>결재문서구분코드</div>,
                dataIndex: "cd",
                key: "cd",
                align: "center",
                width: "10%",
            },
            {
                title: () => <div style={{ textAlign: "center" }}>결재문서구분명</div>,
                dataIndex: "cdNm",
                key: "cdNm",
                align: "center",
                width: "20%",
            },
            {
                title: () => <div style={{ textAlign: "center" }}>사용여부</div>,
                dataIndex: "useYn",
                key: "useYn",
                align: "center",
                width: "10%",
                render: (text:string) => text === 'Y' ? '사용' : '미사용'
            },
            {
                title: () => <div style={{ textAlign: "center" }}>상세보기</div>,
                key: 'action',
                render: (_:string, record:IApprDocItemState) => (
                    <Button
                        id="btn_showDetail"
                        name="btn_showDetail"
                        className="background-color-default-button"
                        size="small"
                        onClick={(e) => onClick(e, record)}
                    >
                        상세보기
                    </Button>
                ),
                align: 'center',
                width: '9%',
            }];
            return arr;
    }, []);

    /**
     * 기능 : 초기화 이벤트 선언
     * param : 
     */
    const fn_Reset = () => {
        dispatch(Com0110mAction.initState());
    };

    /**
     * 기능 : 결과초기화 이벤트 선언
     * param : 
     */
    const fn_ResetResult = () => {
        dispatch(Com0110mAction.initState());
    };


    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any, record:IApprDocItemState) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btn_Search': {
                fn_Search();
                break;
            }

            case 'btn_Reset': {
                fn_Reset();
                break;
            }

            case 'btn_addApprDoc': {
                const params:any = {};
                params.title = '결재문서등록';
                params.width = 1000
                params.popupId = 'com0110p';
                params.accept = () => fn_Search();
                params.cancel = () => fn_Search();
                common.openPopup(params);
                break;

            }
            case 'btn_showDetail': {
                const params:any = {};
                params.title = '결재문서관리';
                params.width = 1000
                params.popupId = 'com0110p';
                params.accept = () => fn_Search();
                params.cancel = () => fn_Search();
                params.props = { cd : record.cd };
                common.openPopup(params);
                break;
            }

            default:
        }
    };

    /**
     * 기능 : 검색조회
     * param : 
     */
    const fn_Search = () => {
        dispatch(Com0110mAction.setCurrent(1));

        const option = {
            useYn: 'ALL',
            callback: (datas:IApprDocItemState[]) => {
                const codeItems = datas.filter(e => {
                    return e.cd !== '001';
                });
                dispatch(Com0110mAction.setApprDocList(codeItems));
            }
        };
        common.getComCodeList('014', option);
    };


    return (
        <>
            <Layout>
                <Content>
                    <Row align='middle' style={{ height: 50 }}>
                        <Col flex='9.5' >
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>결재문서목록</Text>
                            </Space>
                        </Col>
                        <Col flex='0.5' style={{ marginRight: 15 }}>
                            <Space>
                                <Button
                                    name='btn_Reset'
                                    className='background-color-default-button'
                                    onClick={(e:any) => onClick(e, null)}
                                >
                                    초기화
                                </Button>
                                <Button
                                    name='btn_Search'
                                    className='background-color-primary-button'
                                    icon={<SearchOutlined />}
                                    onClick={(e:any) => onClick(e, null)}
                                >
                                    조회
                                </Button>
                                {common.isAdmin() ?
                                <Button
                                    className='background-color-primary-button'
                                    onClick={(e:any) => onClick(e, null)}
                                    name='btn_addApprDoc'
                                >
                                    + 결재문서등록
                                </Button> : null}
                            </Space>
                        </Col>
                    </Row>
                    <Table dataSource={apprDocList}
                        key='cd'
                        columns={columns()}
                        size='small'
                        style={{width:'100%'}}
                        scroll={{y:550}}
                        pagination={{
                            pageSize: pageSize,
                            current: current,
                            showTotal : () => '총 ' + apprDocList.length + ' 건',
                            onChange: (page, pageSize) => {
                                dispatch(Com0110mAction.setCurrent(page));
                                dispatch(Com0110mAction.setPageSize(pageSize));
                            },
                        }}
                    />
                </Content>
            </Layout>
        </>
    );
};

export default Com0108m;

